var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useEffect } from 'react';
import Button from '../base/Button/Button';
import { ConfirmModal } from '../Modal';
import { IconArrow } from '../base/Icons/IconArrow';
import { IconTrash } from '../base/Icons/IconTrash';
import './styles.css';
var MAX_SELECTED_TECHNICIANS = 3;
export function List(_a) {
    var contacts = _a.contacts, onCallOrderChange = _a.onCallOrderChange, onSave = _a.onSave, from = _a.from, rotationList = _a.rotationList, _b = _a.readOnly, readOnly = _b === void 0 ? false : _b;
    var _c = useState([]), selectedTechnicians = _c[0], setSelectedTechnicians = _c[1];
    var _d = useState(null), draggedTechnician = _d[0], setDraggedTechnician = _d[1];
    var _e = useState(null), confirmDialog = _e[0], setConfirmDialog = _e[1];
    // Check if rotation list is currently set
    var isRotationListSet = !!((rotationList === null || rotationList === void 0 ? void 0 : rotationList.primary_contact) && (rotationList === null || rotationList === void 0 ? void 0 : rotationList.final_contact));
    // Initialize selectedTechnicians from rotationList when it changes
    useEffect(function () {
        if (rotationList && contacts.length > 0) {
            var initialSelected = [];
            // Find primary contact
            var primaryContact = contacts.find(function (c) { return c.id === rotationList.primary_contact; });
            if (primaryContact)
                initialSelected.push(primaryContact);
            // Find backup contact (if exists)
            if (rotationList.backup_contact) {
                var backupContact = contacts.find(function (c) { return c.id === rotationList.backup_contact; });
                if (backupContact)
                    initialSelected.push(backupContact);
            }
            // Find final contact
            var finalContact = contacts.find(function (c) { return c.id === rotationList.final_contact; });
            if (finalContact)
                initialSelected.push(finalContact);
            setSelectedTechnicians(initialSelected);
        }
    }, [rotationList, contacts]);
    // Get contact category label based on index and total count
    var getContactCategory = function (index, totalCount) {
        if (totalCount === 2) {
            return index === 0 ? 'Primary' : 'Final';
        }
        if (totalCount === 3) {
            if (index === 0)
                return 'Primary';
            if (index === 1)
                return 'Secondary';
            return 'Final';
        }
        // For 1 technician (invalid state but handle it)
        return 'Primary';
    };
    // Validate that required contacts are present
    // Allow saving empty list (to delete rotation) or valid list with 2 or 3 technicians
    // 2 technicians = Primary + Final, 3 technicians = Primary + Secondary + Final
    var isValidRotation = function () { return selectedTechnicians.length === 0
        || selectedTechnicians.length === 2
        || selectedTechnicians.length === 3; };
    // Get available technicians (those not in selected list)
    var getAvailableTechnicians = function () {
        var selectedIds = new Set(selectedTechnicians.map(function (t) { return t.id; }));
        return (contacts !== null && contacts !== void 0 ? contacts : []).filter(function (tech) { return !selectedIds.has(tech.id); });
    };
    var handleAddTechnician = function (technician) {
        if (selectedTechnicians.length < MAX_SELECTED_TECHNICIANS) {
            var newSelected = __spreadArray(__spreadArray([], selectedTechnicians, true), [technician], false);
            setSelectedTechnicians(newSelected);
            onCallOrderChange === null || onCallOrderChange === void 0 ? void 0 : onCallOrderChange(newSelected);
        }
    };
    var handleRemoveTechnician = function (technicianId) {
        var newSelected = selectedTechnicians.filter(function (t) { return t.id !== technicianId; });
        setSelectedTechnicians(newSelected);
        onCallOrderChange === null || onCallOrderChange === void 0 ? void 0 : onCallOrderChange(newSelected);
    };
    var handleClearAll = function () {
        setSelectedTechnicians([]);
        onCallOrderChange === null || onCallOrderChange === void 0 ? void 0 : onCallOrderChange([]);
    };
    var handleDragStart = function (technician) {
        setDraggedTechnician(technician);
    };
    var handleDragEnd = function () {
        setDraggedTechnician(null);
    };
    var handleDropOnSelected = function (e) {
        e.preventDefault();
        if (draggedTechnician && selectedTechnicians.length < MAX_SELECTED_TECHNICIANS) {
            if (!selectedTechnicians.find(function (t) { return t.id === draggedTechnician.id; })) {
                handleAddTechnician(draggedTechnician);
            }
        }
    };
    var handleReorderTechnicians = function (fromIndex, toIndex) {
        if (toIndex < 0 || toIndex >= selectedTechnicians.length)
            return;
        var newSelected = Array.from(selectedTechnicians);
        var movedTechnician = newSelected.splice(fromIndex, 1)[0];
        newSelected.splice(toIndex, 0, movedTechnician);
        setSelectedTechnicians(newSelected);
        onCallOrderChange === null || onCallOrderChange === void 0 ? void 0 : onCallOrderChange(newSelected);
    };
    var REVERT_MESSAGE = 'This will clear the contact list and restore the calendar-based rotations. Are you sure you want to proceed?';
    var OVERWRITE_MESSAGE = 'This will overwrite the existing calendar-based rotations with the selected contacts. Are you sure you want to proceed?';
    var performSave = function () {
        var _a, _b, _c, _d;
        // If empty list, send null to delete
        if (selectedTechnicians.length === 0) {
            onSave === null || onSave === void 0 ? void 0 : onSave(null);
            return;
        }
        // Convert array to RotationList format
        var newRotationList = {
            primary_contact: ((_a = selectedTechnicians[0]) === null || _a === void 0 ? void 0 : _a.id) || '',
            final_contact: selectedTechnicians.length === 2
                ? ((_b = selectedTechnicians[1]) === null || _b === void 0 ? void 0 : _b.id) || ''
                : ((_c = selectedTechnicians[2]) === null || _c === void 0 ? void 0 : _c.id) || '',
        };
        // Add backup_contact only if there are 3 technicians
        if (selectedTechnicians.length === 3) {
            newRotationList.backup_contact = ((_d = selectedTechnicians[1]) === null || _d === void 0 ? void 0 : _d.id) || undefined;
        }
        onSave === null || onSave === void 0 ? void 0 : onSave(newRotationList);
    };
    var handleSave = function () {
        if (!isValidRotation())
            return;
        // Case 1: Saving empty list when previously had contacts (revert scenario)
        if (selectedTechnicians.length === 0 && isRotationListSet) {
            setConfirmDialog({
                open: true,
                title: 'Revert to Calendar View',
                message: REVERT_MESSAGE,
                onConfirm: function () {
                    performSave();
                    setConfirmDialog(null);
                },
            });
            return;
        }
        // Case 2: Saving contacts for the first time (overwriting calendar rotations)
        if (selectedTechnicians.length > 0 && !isRotationListSet) {
            setConfirmDialog({
                open: true,
                title: 'Confirm Changes',
                message: OVERWRITE_MESSAGE,
                onConfirm: function () {
                    performSave();
                    setConfirmDialog(null);
                },
            });
            return;
        }
        // Case 3: Modifying existing contacts (no confirmation needed)
        performSave();
    };
    return (React.createElement("div", { className: "list-view list-view-".concat(from), "data-testid": "tech-rotation-list-view" },
        React.createElement("div", { className: "available-panel", "data-testid": "tech-rotation-available-panel" },
            React.createElement("h4", null, "Available Technicians"),
            React.createElement("div", { className: "technicians-list", "data-testid": "tech-rotation-available-list" }, getAvailableTechnicians().length === 0 ? (React.createElement("p", { className: "empty-message" }, "All technicians have been selected")) : (getAvailableTechnicians().map(function (technician) { return (React.createElement("div", { key: technician.id, className: "technician-item available-item", draggable: true, onDragStart: function () { return handleDragStart(technician); }, onDragEnd: handleDragEnd, "data-testid": "tech-rotation-available-item-".concat(technician.id) },
                React.createElement("div", { className: "technician-info-list" },
                    React.createElement("span", { className: "technician-name" }, technician.name),
                    React.createElement("span", { className: "technician-phone" }, technician.primaryPhoneNumber)),
                React.createElement(Button, { type: "button", variant: "contained", size: "small", onClick: function () { return handleAddTechnician(technician); }, disabled: readOnly || selectedTechnicians.length >= MAX_SELECTED_TECHNICIANS, "data-testid": "tech-rotation-add-technician-".concat(technician.id) }, "Add"))); })))),
        React.createElement("div", { className: "selected-panel", "data-testid": "tech-rotation-selected-panel" },
            React.createElement("div", { className: "selected-header" },
                React.createElement("h4", null,
                    "Call Order",
                    ' ',
                    React.createElement("span", { className: "count", "data-testid": "tech-rotation-call-order-count" },
                        "(",
                        selectedTechnicians.length,
                        "/",
                        MAX_SELECTED_TECHNICIANS,
                        ")")),
                React.createElement("div", { className: "header-actions" },
                    selectedTechnicians.length > 0 && (React.createElement(Button, { type: "button", variant: "outlined", size: "small", onClick: handleClearAll, "data-testid": "tech-rotation-clear-all-btn" }, "Clear All")),
                    React.createElement(Button, { type: "button", variant: "contained", size: "small", onClick: handleSave, disabled: readOnly || !isValidRotation(), "data-testid": "tech-rotation-list-save-btn" }, "Save"))),
            React.createElement("div", { className: "selected-drop-zone", onDragOver: function (e) { return e.preventDefault(); }, onDrop: handleDropOnSelected, "data-testid": "tech-rotation-selected-drop-zone" }, selectedTechnicians.length === 0 ? (React.createElement("div", { className: "empty-slot", "data-testid": "tech-rotation-empty-slot" }, "Add 2-3 technicians: Primary Contact and Final Contact are required. Backup Contact is optional.")) : (React.createElement("div", { className: "selected-list", "data-testid": "tech-rotation-selected-list" }, selectedTechnicians.map(function (technician, index) { return (React.createElement("div", { key: technician.id, className: "selected-item", "data-testid": "tech-rotation-selected-item-".concat(technician.id) },
                React.createElement("div", { className: "contact-category" },
                    React.createElement("span", { className: "category-label", "data-testid": "tech-rotation-category-".concat(index) }, getContactCategory(index, selectedTechnicians.length))),
                React.createElement("div", { className: "technician-info-list" },
                    React.createElement("span", { className: "technician-name" }, technician.name),
                    React.createElement("span", { className: "technician-phone" }, technician.primaryPhoneNumber)),
                React.createElement("div", { className: "item-actions" },
                    index > 0 && (React.createElement("button", { type: "button", className: "icon-btn", onClick: function () { return handleReorderTechnicians(index, index - 1); }, title: "Move up", "data-testid": "tech-rotation-move-up-".concat(technician.id) },
                        React.createElement(IconArrow, { direction: "up", size: "small" }))),
                    index < selectedTechnicians.length - 1 && (React.createElement("button", { type: "button", className: "icon-btn", onClick: function () { return handleReorderTechnicians(index, index + 1); }, title: "Move down", "data-testid": "tech-rotation-move-down-".concat(technician.id) },
                        React.createElement(IconArrow, { direction: "down", size: "small" }))),
                    React.createElement("button", { type: "button", className: "trash-btn", onClick: function () { return handleRemoveTechnician(technician.id); }, title: "Remove technician", "data-testid": "tech-rotation-remove-".concat(technician.id) },
                        React.createElement(IconTrash, { size: "small" }))))); }))))),
        React.createElement(ConfirmModal, { open: !!(confirmDialog === null || confirmDialog === void 0 ? void 0 : confirmDialog.open), title: (confirmDialog === null || confirmDialog === void 0 ? void 0 : confirmDialog.title) || '', message: (confirmDialog === null || confirmDialog === void 0 ? void 0 : confirmDialog.message) || '', confirmText: "Confirm", cancelText: "Cancel", onConfirm: function () { return confirmDialog === null || confirmDialog === void 0 ? void 0 : confirmDialog.onConfirm(); }, onCancel: function () { return setConfirmDialog(null); }, fromApp: from })));
}
//# sourceMappingURL=List.js.map